import { getAvailablePricingConfigurations } from '../data/platform-pricing.config';
import type {
  FundraiserScenario,
  FundraisingCalculationSnapshot,
  FundraisingComparisonState,
  PlatformComparisonResult,
  PlatformId,
  PlatformPricingConfiguration,
} from '../models/fundraising-comparison.models';
import { PricingConfigurationResolver } from './pricing-configuration-resolver';
import { PricingEngine } from './pricing-engine';
import { ScenarioFactory } from './scenario-factory';

export class FundraisingCalculationCoordinator {
  private readonly scenarioFactory = new ScenarioFactory();
  private readonly pricingEngine = new PricingEngine();
  private readonly pricingResolver = new PricingConfigurationResolver();

  calculate(
    state: FundraisingComparisonState,
    platformIds: PlatformId[],
  ): FundraisingCalculationSnapshot {
    const scenario = this.scenarioFactory.createScenario(
      state.intendedGiftTotalCents,
      state.profileId,
      state.donationCountOverride,
    );
    const normalizedState: FundraisingComparisonState = {
      ...state,
      intendedGiftTotalCents: scenario.intendedGiftTotalCents,
      profileId: scenario.profileId ?? state.profileId,
      donationCountOverride:
        state.donationCountOverride === null ||
        scenario.transactions.length === 0
          ? null
          : scenario.transactions.length,
    };

    const configurationsById = new Map<
      PlatformId,
      PlatformPricingConfiguration
    >(
      getAvailablePricingConfigurations().map((configuration) => [
        configuration.id,
        configuration,
      ]),
    );
    const results = [...new Set(platformIds)]
      .map((platformId) => configurationsById.get(platformId))
      .filter(
        (
          configuration,
        ): configuration is PlatformPricingConfiguration =>
          Boolean(configuration),
      )
      .map((configuration) =>
        this.pricingEngine.compare(
          scenario,
          this.pricingResolver.resolve(
            configuration,
            normalizedState.optionSelections[configuration.id],
          ),
        ),
      );

    return { state: normalizedState, scenario, results };
  }

  calculatePlatformConfigurations(
    scenario: FundraiserScenario,
    platformId: PlatformId,
  ): PlatformComparisonResult[] {
    const configuration = getAvailablePricingConfigurations().find(
      (platform) => platform.id === platformId,
    );
    if (!configuration) return [];

    return this.pricingResolver
      .enumerateOptionSelections(configuration)
      .map((optionSelections) =>
        this.pricingEngine.compare(
          scenario,
          this.pricingResolver.resolve(configuration, optionSelections),
        ),
      );
  }
}
